import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
} from "react";
import HTMLFlipBook from "react-pageflip";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Home, BookOpen, Hand } from "lucide-react";

import CoverPage from "@/components/catalog/CoverPage";
import SummaryPage from "@/components/catalog/SummaryPage";
import ModalityPage from "@/components/catalog/ModalityPage";
import ContactPage from "@/components/catalog/ContactPage";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

const PageWrap = forwardRef(({ children }, ref) => (
  <div ref={ref} className="magazine-page w-full h-full">
    {children}
  </div>
));
PageWrap.displayName = "PageWrap";

// Hinged page-turn (mobile): the page swings around the spine edge in 3D.
const flipVariants = {
  enter: (d) => ({ rotateY: d >= 0 ? 108 : -108, opacity: 0 }),
  center: { rotateY: 0, opacity: 1 },
  exit: (d) => ({ rotateY: d >= 0 ? -108 : 108, opacity: 0 }),
};

export default function Flipbook({ studio, modalities }) {
  const isMobile = useIsMobile();
  const bookRef = useRef(null);
  const [page, setPage] = useState(0);
  const [dir, setDir] = useState(0);
  const [hintDone, setHintDone] = useState(false);
  const pageRef = useRef(0);
  pageRef.current = page;
  const draggedRef = useRef(false);

  const pageDefs = useMemo(() => {
    const arr = [{ type: "cover" }, { type: "summary" }];
    (modalities || []).forEach((m) => arr.push({ type: "modality", modality: m }));
    arr.push({ type: "contact" });
    return arr;
  }, [modalities]);

  const total = pageDefs.length;
  const dismissHint = useCallback(() => setHintDone(true), []);

  const goToPage = useCallback(
    (target) => {
      dismissHint();
      const t = Math.max(0, Math.min(total - 1, target));
      if (isMobile) {
        setDir(t >= pageRef.current ? 1 : -1);
        setPage(t);
      } else {
        const pf = bookRef.current?.pageFlip();
        if (!pf) return;
        pf.turnToPage(t);
        setPage(t);
      }
    },
    [dismissHint, isMobile, total]
  );

  const next = useCallback(() => {
    dismissHint();
    if (isMobile) {
      if (pageRef.current < total - 1) {
        setDir(1);
        setPage(pageRef.current + 1);
      }
    } else {
      bookRef.current?.pageFlip()?.flipNext();
    }
  }, [dismissHint, isMobile, total]);

  const prev = useCallback(() => {
    dismissHint();
    if (isMobile) {
      if (pageRef.current > 0) {
        setDir(-1);
        setPage(pageRef.current - 1);
      }
    } else {
      bookRef.current?.pageFlip()?.flipPrev();
    }
  }, [dismissHint, isMobile]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const renderContent = (def, pageNumber) => {
    switch (def.type) {
      case "cover":
        return <CoverPage studio={studio} onOpen={() => goToPage(1)} />;
      case "summary":
        return (
          <SummaryPage
            studio={studio}
            modalities={modalities}
            onSelect={(idx) => goToPage(2 + idx)}
          />
        );
      case "modality":
        return (
          <ModalityPage
            studio={studio}
            modality={def.modality}
            pageNumber={pageNumber}
            totalPages={total}
          />
        );
      case "contact":
        return (
          <ContactPage
            studio={studio}
            pageNumber={pageNumber}
            totalPages={total}
            onHome={() => goToPage(0)}
            onSummary={() => goToPage(1)}
          />
        );
      default:
        return null;
    }
  };

  const showHint = !hintDone && page === 0;
  const progress = ((page + 1) / total) * 100;

  const containerStyle = isMobile
    ? { width: "min(94vw, 430px)", height: "calc(100dvh - 128px)" }
    : { width: "min(96vw, 1080px)", height: "min(84vh, 640px)" };

  return (
    <div className="relative w-full min-h-[100dvh] flex flex-col items-center justify-center overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(212,175,55,0.06),transparent_60%)]" />

      {/* Top discrete quick-nav */}
      <div className="absolute top-4 right-4 z-30 flex gap-2">
        <button
          data-testid="nav-cover-button"
          onClick={() => goToPage(0)}
          aria-label="Voltar à capa"
          className="w-9 h-9 flex items-center justify-center border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-colors bg-black/30 backdrop-blur-sm"
        >
          <Home size={15} strokeWidth={1.4} />
        </button>
        <button
          data-testid="nav-summary-button"
          onClick={() => goToPage(1)}
          aria-label="Ir para o sumário"
          className="w-9 h-9 flex items-center justify-center border border-[rgba(212,175,55,0.3)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-colors bg-black/30 backdrop-blur-sm"
        >
          <BookOpen size={15} strokeWidth={1.4} />
        </button>
      </div>

      {/* Book area */}
      <div className="relative z-10 w-full flex-1 flex items-center justify-center px-2">
        <div className="flex items-center justify-center" style={containerStyle}>
          {isMobile ? (
            <div
              className="relative w-full h-full shadow-2xl"
              style={{ perspective: "1500px" }}
              data-testid="mobile-flip"
            >
              <AnimatePresence initial={false} custom={dir} mode="popLayout">
                <motion.div
                  key={page}
                  custom={dir}
                  variants={flipVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  drag="x"
                  dragDirectionLock
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.4}
                  onDragStart={() => { draggedRef.current = true; }}
                  onDragEnd={(e, info) => {
                    if (info.offset.x < -70 || info.velocity.x < -350) next();
                    else if (info.offset.x > 70 || info.velocity.x > 350) prev();
                    setTimeout(() => { draggedRef.current = false; }, 260);
                  }}
                  onClickCapture={(e) => {
                    if (draggedRef.current) {
                      e.stopPropagation();
                      e.preventDefault();
                      draggedRef.current = false;
                    }
                  }}
                  className="absolute inset-0 magazine-page"
                  style={{
                    transformStyle: "preserve-3d",
                    transformOrigin: dir >= 0 ? "left center" : "right center",
                    backfaceVisibility: "hidden",
                  }}
                >
                  {renderContent(pageDefs[page], page + 1)}
                </motion.div>
              </AnimatePresence>
            </div>
          ) : (
            <HTMLFlipBook
              ref={bookRef}
              width={430}
              height={540}
              size="stretch"
              minWidth={300}
              maxWidth={520}
              minHeight={380}
              maxHeight={660}
              drawShadow={true}
              maxShadowOpacity={0.5}
              showCover={true}
              usePortrait={false}
              flippingTime={750}
              disableFlipByClick={true}
              onFlip={(e) => setPage(e.data)}
              className="magazine drop-shadow-[0_25px_60px_rgba(0,0,0,0.7)]"
            >
              {pageDefs.map((def, i) => (
                <PageWrap key={i}>{renderContent(def, i + 1)}</PageWrap>
              ))}
            </HTMLFlipBook>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="relative z-30 w-full flex items-center justify-center gap-4 pb-5 pt-2">
        <button
          data-testid="prev-page-button"
          onClick={prev}
          aria-label="Página anterior"
          className="w-11 h-11 flex items-center justify-center border border-[rgba(212,175,55,0.35)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-colors rounded-full"
        >
          <ChevronLeft size={20} strokeWidth={1.4} />
        </button>

        <div className="flex flex-col items-center w-40">
          <span
            data-testid="page-indicator"
            className="font-body text-[0.62rem] tracking-[0.3em] text-[var(--nude)] mb-1.5"
          >
            {String(page + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <div className="w-full h-[2px] bg-[rgba(212,175,55,0.15)]">
            <motion.div
              className="h-full bg-[var(--gold)]"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>

        <button
          data-testid="next-page-button"
          onClick={next}
          aria-label="Próxima página"
          className="w-11 h-11 flex items-center justify-center border border-[rgba(212,175,55,0.35)] text-[var(--nude)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-colors rounded-full"
        >
          <ChevronRight size={20} strokeWidth={1.4} />
        </button>
      </div>

      {/* First-open hint */}
      <AnimatePresence>
        {showHint && (
          <motion.div
            data-testid="swipe-hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="absolute bottom-24 z-40 pointer-events-none flex flex-col items-center"
          >
            <motion.div
              animate={{ x: [0, 16, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="text-[var(--gold)]"
            >
              <Hand size={22} strokeWidth={1.3} />
            </motion.div>
            <p className="mt-2 font-body text-[0.6rem] uppercase tracking-[0.24em] text-[var(--nude)] text-center max-w-[15rem] px-4">
              Arraste para o lado para conhecer nossos procedimentos
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
